import {
  CURATED_FRESHNESS_DAYS,
  SENIORITY_LABELS,
  fmtRecent,
  label,
  regionsLabel,
  type CuratedRoleInput,
} from '@remote-medium/shared';
import {
  ApiRequestError,
  Badge,
  Button,
  RoleForm,
  errorMessage,
  unwrap,
  useApi,
} from '@remote-medium/ui';
import { Plus } from 'lucide-react';
import { useState } from 'react';
import { Link, useSearchParams } from 'react-router';

import { admin } from '../../lib/client.ts';
import { RoleStatusChip } from '../../lib/status-chip.tsx';
import {
  Card,
  ConfirmButton,
  DataTable,
  EmptyRow,
  ExternalLink,
  Notice,
  Page,
  PageHeader,
  ROW_LINK,
  SectionHeader,
  StatusRow,
  Tabs,
  tableReady,
} from '../../lib/ui.tsx';

type Kind = 'all' | 'company' | 'curated' | 'stale';

async function loadRoles() {
  const [roles, stale] = await Promise.all([
    unwrap(admin.roles.$get({ query: {} })),
    unwrap(admin.curated.stale.$get()),
  ]);
  return { roles, stale };
}

function External() {
  return (
    <Badge variant="external" className="font-normal">
      External
    </Badge>
  );
}

export function RolesPage() {
  const [params, setParams] = useSearchParams();
  const kind = (params.get('kind') as Kind | null) ?? 'all';
  const { data, error, loading, reload } = useApi(loadRoles, []);
  const { data: taxonomy } = useApi(() => unwrap(admin.taxonomy.$get()), []);
  const [adding, setAdding] = useState(false);
  const [msg, setMsg] = useState<{ tone: 'info' | 'error'; text: string } | null>(null);

  const ready = tableReady({ data, loading, error });
  const roles = data?.roles ?? [];
  const stale = data?.stale ?? [];
  const shown = kind === 'all' || kind === 'stale' ? roles : roles.filter((r) => r.kind === kind);
  const count = (k: 'company' | 'curated') => roles.filter((r) => r.kind === k).length;

  const verify = (id: string) =>
    unwrap(admin.roles[':id'].verify.$post({ param: { id } }))
      .then(() => {
        setMsg({ tone: 'info', text: 'Marked verified today.' });
        reload();
      })
      .catch((e: unknown) => setMsg({ tone: 'error', text: errorMessage(e) }));

  return (
    <Page className="gap-6">
      <PageHeader
        title="Roles"
        description={`Company roles are ranked against the pool every night. Curated roles are external listings you post by hand and re-check every ${CURATED_FRESHNESS_DAYS} days.`}
        actions={
          !adding && (
            <Button size="sm" onClick={() => setAdding(true)}>
              <Plus data-icon="inline-start" />
              Post curated role
            </Button>
          )
        }
      />

      {adding && taxonomy && (
        <Card>
          <SectionHeader title="New curated role" />
          <p className="m-0 max-w-2xl text-sm text-muted-foreground">
            Found on a company careers page? Enter the facts and write a one or two sentence summary
            in your own words. Never paste the description. Link to the original posting.
          </p>
          <RoleForm
            taxonomy={taxonomy}
            curated
            submitLabel="Publish listing"
            onCancel={() => setAdding(false)}
            onSubmit={async (input) => {
              setMsg(null);
              try {
                await unwrap(admin.roles.curated.$post({ json: input as CuratedRoleInput }));
                setAdding(false);
                setMsg({ tone: 'info', text: 'Listing published.' });
                reload();
              } catch (e) {
                setMsg({
                  tone: 'error',
                  text: e instanceof ApiRequestError ? e.message : 'Could not publish.',
                });
              }
            }}
          />
        </Card>
      )}

      <Tabs
        label="Filter by kind"
        value={kind}
        onChange={(v) => setParams(v === 'all' ? {} : { kind: v }, { replace: true })}
        tabs={[
          { value: 'all', label: 'All', count: data ? roles.length : undefined },
          { value: 'company', label: 'Company', count: data ? count('company') : undefined },
          { value: 'curated', label: 'Curated', count: data ? count('curated') : undefined },
          { value: 'stale', label: 'Needs re-check', count: data?.stale.length },
        ]}
      />

      {msg && <Notice tone={msg.tone}>{msg.text}</Notice>}

      {kind === 'stale' && (
        <DataTable minWidth="min-w-2xl">
          <thead>
            <tr>
              <th>Listing</th>
              <th>Last verified</th>
              <th>
                <span className="sr-only">Actions</span>
              </th>
            </tr>
          </thead>
          <tbody>
            <StatusRow colSpan={3} loading={loading} error={error} />
            {ready && stale.length === 0 && (
              <EmptyRow colSpan={3}>
                Nothing to re-check. Every open listing was verified in the last{' '}
                {CURATED_FRESHNESS_DAYS} days.
              </EmptyRow>
            )}
            {ready &&
              stale.map((r) => (
                <tr key={r.id} className="hover:bg-muted/50">
                  <td>
                    <Link to={`/roles/${r.id}`} className={ROW_LINK}>
                      {r.title}
                    </Link>
                    <div className="flex flex-wrap items-center gap-x-2 text-muted-foreground">
                      {r.sourceCompanyName}
                      {r.sourceUrl && <ExternalLink href={r.sourceUrl}>Original</ExternalLink>}
                    </div>
                  </td>
                  <td className="whitespace-nowrap text-muted-foreground">
                    {r.lastSeenAt ? fmtRecent(r.lastSeenAt) : 'Never'}
                  </td>
                  <td>
                    <div className="flex justify-end gap-2">
                      <ConfirmButton
                        variant="ghost"
                        title="Close this listing?"
                        description="It comes off the public site and out of digests. Do this when the original posting is gone."
                        confirmLabel="Close listing"
                        destructive
                        onConfirm={async () => {
                          await unwrap(admin.roles[':id'].close.$post({ param: { id: r.id } }));
                          setMsg({ tone: 'info', text: 'Listing closed.' });
                          reload();
                        }}
                      >
                        Close
                      </ConfirmButton>
                      <Button size="sm" variant="outline" onClick={() => void verify(r.id)}>
                        Still live
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
          </tbody>
        </DataTable>
      )}

      {kind !== 'stale' && (
        <DataTable>
          <thead>
            <tr>
              <th>Role</th>
              <th>Status</th>
              <th>Type</th>
              <th>Regions</th>
              <th>Posted</th>
            </tr>
          </thead>
          <tbody>
            <StatusRow colSpan={5} loading={loading} error={error} />
            {ready && shown.length === 0 && <EmptyRow colSpan={5}>No roles here yet.</EmptyRow>}
            {ready &&
              shown.map((r) => {
                const owner = r.kind === 'curated' ? r.sourceCompanyName : r.companyName;
                return (
                  <tr key={r.id} className="hover:bg-muted/50">
                    <td className="max-w-80">
                      <div className="flex flex-wrap items-center gap-2">
                        <Link to={`/roles/${r.id}`} className={ROW_LINK}>
                          {r.title}
                        </Link>
                        {r.kind === 'curated' && <External />}
                      </div>
                      <div className="truncate text-muted-foreground">
                        {[owner, r.seniority && label(SENIORITY_LABELS, r.seniority)]
                          .filter(Boolean)
                          .join(' · ')}
                      </div>
                    </td>
                    <td>
                      <RoleStatusChip status={r.status} />
                    </td>
                    <td className="whitespace-nowrap">
                      {r.roleTypeFamily} · {r.roleTypeName}
                    </td>
                    <td className="max-w-56 text-muted-foreground">{regionsLabel(r.regions)}</td>
                    <td className="whitespace-nowrap text-muted-foreground tabular-nums">
                      {fmtRecent(r.createdAt)}
                    </td>
                  </tr>
                );
              })}
          </tbody>
        </DataTable>
      )}
    </Page>
  );
}
